import Dexie from "dexie";
import { api } from "./api.js";

// Offline-first plumbing: failed writes queue here and replay when we're back
// online (server-side client_id idempotency makes replays safe). Reads cache
// their last good response so the app shows data with no signal.
export const localdb = new Dexie("business-tracker-local");
localdb.version(1).stores({
  outbox: "++qid, createdAt",
  cache: "key",
});

const listeners = new Set();
export function onOutboxChange(fn) {
  listeners.add(fn);
  notify();
  return () => listeners.delete(fn);
}
async function notify() {
  const n = await localdb.outbox.count().catch(() => 0);
  listeners.forEach((f) => f(n));
}

export async function enqueue(method, path, body) {
  await localdb.outbox.add({ method, path, body, createdAt: Date.now(), attempts: 0 });
  notify();
}

let flushing = false;
export async function flushOutbox() {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  try {
    const items = await localdb.outbox.orderBy("createdAt").toArray();
    for (const it of items) {
      try {
        await api(it.path, { method: it.method, body: it.body });
        await localdb.outbox.delete(it.qid);
      } catch (e) {
        if (e.status && e.status >= 400 && e.status < 500 && e.status !== 401 && e.status !== 429) {
          // Permanent rejection (validation etc.) — replaying won't help; drop it.
          await localdb.outbox.delete(it.qid);
          continue;
        }
        // Network failure, auth expired, rate-limited, or 5xx: stop and retry later.
        await localdb.outbox.update(it.qid, {
          attempts: (it.attempts || 0) + 1,
          lastError: e.message || String(e.status || "network"),
        });
        break;
      }
    }
  } finally {
    flushing = false;
    notify();
  }
}

// True only for "the network itself failed" (offline), never for server errors.
export function isNetworkError(err) {
  return !err?.status;
}

// GET with a stale-read fallback: last good response is served when offline.
export async function cachedGet(path) {
  try {
    const data = await api(path);
    await localdb.cache.put({ key: path, data, at: Date.now() }).catch(() => {});
    return { data, stale: false };
  } catch (err) {
    if (isNetworkError(err)) {
      const hit = await localdb.cache.get(path).catch(() => null);
      if (hit) return { data: hit.data, stale: true };
    }
    throw err;
  }
}

// Wire the flush triggers once per app load.
export function initOutbox() {
  window.addEventListener("online", () => flushOutbox());
  flushOutbox();
}
